import { Delete, DeleteForever, Undo } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "common/locales";

import { FormattedMessage } from "react-intl";

import { Create } from "@mui/icons-material";
import { DrawControlButton } from "./DrawControlButton";
import handleSelectedImg from "./handle-selected.png";
import handleImg from "./handle.png";

import { styled } from "@mui/material/styles";

export const Img = styled("img")(({ theme }) => ({
  maxHeight: "3rem",
  width: "auto",
  verticalAlign: "middle",
  borderRadius: theme.spacing(0.5),
  margin: `${theme.spacing(0.5)} 0`,
}));

export const DrawControlButtonExtended = styled(DrawControlButton)({
  cursor: "auto",
});

export const ModalCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const ModalCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    [theme.breakpoints.up("sm")]: {
      minWidth: "450px",
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: "env(safe-area-inset-top)",
      paddingBottom: "calc(64px + env(safe-area-inset-bottom))",
    },
  },
}));

export const ZoomControlContainer = styled("span")(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
}));

export const FlexTypography = styled("div")({
  display: "flex",
  alignItems: "center",
});

interface Props {
  isOpen: boolean;
  handleClose: () => void;
}

export function DrawingHelpModal({ isOpen, handleClose }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getButton = (icon: JSX.Element) => (
    <DrawControlButtonExtended
      variant="contained"
      size="small"
      disableElevation
      disableRipple
      color="drawTool"
    >
      {icon}
    </DrawControlButtonExtended>
  );

  const pencilButton = () => getButton(<Create />);
  const undoButton = () => getButton(<Undo />);
  const deleteButton = () => getButton(<Delete />);
  const deleteForeverButton = () => getButton(<DeleteForever />);

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleClose}
      scroll="paper"
      fullScreen={isMobile}
    >
      <DialogTitle id="dialog-title">
        <span role="img" aria-label="Pencil">
          ✏️
        </span>{" "}
        {t("map.drawfield.help.modal.title")}
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            marginBottom: "16px",
          }}
        >
          {t("map.drawfield.help.modal.welcome")}{" "}
          <span role="img" aria-label="point-right">
            👉
          </span>{" "}
          <MuiLink href="mailto:support@ruumi.io" underline="hover">
            {t("map.drawfield.help.modal.link")}
          </MuiLink>
        </Typography>

        <Typography
          variant="h5"
          sx={{
            marginBottom: "16px",
          }}
        >
          {t("map.drawfield.help.modal.createMode.title")}
        </Typography>

        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.createMode.description"
                values={{
                  pencilButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography component="div">
              <FlexTypography>
                <FormattedMessage
                  id="map.drawfield.help.modal.createMode.zoomControls.description"
                  values={{
                    navIcon: () => ZoomControls,
                  }}
                />
              </FlexTypography>
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.createMode.undo.description"
                values={{
                  undoButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage id="map.drawfield.help.modal.createMode.boundaryCorrectness.description" />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.createMode.finish.description"
                values={{
                  pencilButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>

        <Typography
          variant="h5"
          sx={{
            marginBottom: "16px",
          }}
        >
          {t("map.drawfield.help.modal.editMode.title")}
        </Typography>

        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.editMode.description"
                values={{
                  pencilButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.editMode.move.description"
                values={{
                  movePointImg: () => <Img src={handleImg} />,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.editMode.select.description"
                values={{
                  selectPointImg: () => <Img src={handleSelectedImg} />,
                  deleteButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography component="div">
              <FlexTypography>
                <FormattedMessage
                  id="map.drawfield.help.modal.createMode.zoomControls.description"
                  values={{
                    navIcon: () => ZoomControls,
                  }}
                />
              </FlexTypography>
            </Typography>
          </ModalCardContent>
        </ModalCard>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.editMode.finish.description"
                values={{
                  pencilButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>

        <Typography variant="h5">
          {t("map.drawfield.help.modal.deleteBoundary.title")}
        </Typography>
        <ModalCard square>
          <ModalCardContent>
            <Typography
              sx={{
                marginBottom: "16px",
              }}
            >
              <FormattedMessage
                id="map.drawfield.help.modal.deleteBoundary.description"
                values={{
                  deleteButton: deleteForeverButton,
                }}
              />
            </Typography>
          </ModalCardContent>
        </ModalCard>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={handleClose}>
          {t("map.drawfield.help.modal.close")}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}

export default DrawingHelpModal;

const ZoomControls = (
  <ZoomControlContainer>
    <div className="maplibregl-ctrl maplibregl-ctrl-group">
      <button
        className="maplibregl-ctrl-icon maplibregl-ctrl-zoom-in"
        type="button"
        title="Zoom In"
      >
        <span className="maplibregl-ctrl-icon" aria-hidden="true"></span>
      </button>
      <button
        className="maplibregl-ctrl-icon maplibregl-ctrl-zoom-out"
        type="button"
        title="Zoom Out"
      >
        <span className="maplibregl-ctrl-icon" aria-hidden="true"></span>
      </button>
    </div>
  </ZoomControlContainer>
);
